/**
 * Shared presentation + chargeability rules for a contact's stored payment
 * methods.
 *
 * These lived as module-local helpers inside `ContactPaymentMethodsSection`,
 * which is why the invoice Record-payment picker grew its own copy that dropped
 * `label` and `type` and rendered two indistinguishable `VISA •••• 4242` rows.
 * One home so the two surfaces cannot drift again.
 */

/** Which processors this tenant is allowed to transact through. */
export interface AvailableProcessors {
  stripe: boolean;
  payabli: boolean;
}

/**
 * The fields these helpers read, stated structurally rather than as a `Pick` of
 * `ContactPaymentMethod`: the same rules have to hold for a legacy row that
 * reached the UI without a `provider` at all, and for the portal's
 * display-only `PublicSavedPaymentMethod`.
 */
export interface SavedMethodProviderFields {
  provider?: string | null;
}

export interface SavedMethodDisplayFields {
  type?: string | null;
  brand?: string | null;
  last4?: string | null;
  label?: string | null;
  isDefault?: boolean | null;
}

export function isBankPaymentMethod(method: Pick<SavedMethodDisplayFields, 'type'>): boolean {
  return /bank|ach/i.test(method.type ?? '');
}

/**
 * Whether a saved-method charge came back as an accepted bank debit: PENDING on
 * a bank rail. For a bank account that is the normal answer, not an unknown
 * one — the processor took the debit and the funds clear business days later,
 * so from the operator's side the charge is done. A PENDING card is different:
 * its outcome is still being verified.
 *
 * The rail is read from the server's answer, which re-resolves it from the
 * stored method, falling back to the saved method's own type.
 */
export function isAcceptedBankDebit(
  payment: { status?: unknown; paymentMethod?: unknown } | undefined,
  savedMethodType?: string | null,
): boolean {
  if (payment?.status !== 'PENDING') return false;
  const rail =
    typeof payment.paymentMethod === 'string' && payment.paymentMethod.trim()
      ? payment.paymentMethod
      : savedMethodType;
  return isBankPaymentMethod({ type: rail });
}

/**
 * A payment that is a bank debit on its normal path to settling: the processor
 * took it and the funds land business days later. Lists show it as "Clearing"
 * so a bare "Pending" does not read as stuck while the processor's own portal
 * already says approved.
 *
 * Same rule as the invoice's settling banner (`settling-payment.util.ts`): a
 * Payabli debit only counts once it carries the processor's transaction id,
 * and a Stripe one only once a webhook confirmed it (`updatedBy` is
 * `system:webhook`) — a Stripe row exists from before the customer confirms.
 * Without that evidence the outcome is unknown, and calling it "clearing"
 * would stop anyone investigating it.
 */
export function isClearingBankPayment(payment: {
  status?: unknown;
  paymentMethod?: unknown;
  gatewayProvider?: string | null;
  gatewayTxnId?: string | null;
  updatedBy?: string | null;
}): boolean {
  const provider = payment.gatewayProvider?.toLowerCase();
  if (provider === 'payabli') {
    return isAcceptedBankDebit(payment) && Boolean(payment.gatewayTxnId);
  }
  // The webhook marker alone makes it a bank debit: it is only stamped for an
  // async confirmation, and a card settles inside Checkout. The row's method
  // cannot say so — a Checkout offering both rails records CARD whichever the
  // payer chose (the backend's `settling-payment.util.ts` reads it the same way).
  if (provider === 'stripe') {
    return payment.status === 'PENDING' && payment.updatedBy === STRIPE_WEBHOOK_ACTOR;
  }
  return false;
}

/**
 * A bank payment staff cancelled before it cleared (Cancel bank payment): stored
 * FAILED, because nothing was taken, and stamped `cancelledByStaffAt`. The
 * contact payments list and activity feed send the stamp as a field of their
 * own; every other payment response carries it in `gatewayMetadata`.
 */
export function isStaffCancelledPayment(payment: {
  status?: unknown;
  gatewayMetadata?: unknown;
  cancelledByStaffAt?: string | null;
}): boolean {
  if (String(payment.status ?? '').toUpperCase() !== 'FAILED') return false;
  if (typeof payment.cancelledByStaffAt === 'string') return true;
  const metadata = payment.gatewayMetadata;
  return (
    typeof metadata === 'object' &&
    metadata !== null &&
    typeof (metadata as Record<string, unknown>).cancelledByStaffAt === 'string'
  );
}

/** The actor a Stripe webhook stamps when it confirms a debit is in flight. */
const STRIPE_WEBHOOK_ACTOR = 'system:webhook';

function titleCase(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

/**
 * The instrument's headline: `VISA •••• 5556`, `Bank account •••• 6789`.
 * `titleCase` never lower-cases, so a stored `VISA` stays `VISA` and a stored
 * `visa` becomes `Visa` — matching what the contact detail page has always
 * rendered.
 */
export function formatSavedMethodName(
  method: Pick<SavedMethodDisplayFields, 'type' | 'brand' | 'last4'>,
): string {
  const brand = isBankPaymentMethod(method)
    ? 'Bank account'
    : method.brand
      ? titleCase(method.brand)
      : 'Card';
  return method.last4 ? `${brand} •••• ${method.last4}` : brand;
}

/**
 * Credit vs debit, when the stored type actually says. Not cosmetic: on a
 * surcharging tenant the card service fee is barred on debit, so credit and
 * debit are two different prices to the customer and the operator has to be
 * able to tell which one they are about to charge. `null` when the stored type
 * carries no funding answer (legacy `CARD`, Stripe's own `card`) rather than
 * guessing one.
 */
export function formatSavedMethodTypeLabel(
  method: Pick<SavedMethodDisplayFields, 'type'>,
): string | null {
  switch (String(method.type ?? '').toUpperCase()) {
    case 'CREDIT_CARD':
      return 'Credit card';
    case 'DEBIT_CARD':
      return 'Debit card';
    default:
      return null;
  }
}

/**
 * One-line label for a `<SelectItem>`: everything that distinguishes this row
 * from the contact's other rows. The contact detail page shows the same facts
 * across two lines (name, then `Exp 09/27 · QA debit card`); a select option
 * has one line, so they join with the same `·` separator.
 */
export function formatSavedMethodOptionLabel(method: SavedMethodDisplayFields): string {
  const parts = [formatSavedMethodName(method)];
  const typeLabel = formatSavedMethodTypeLabel(method);
  if (typeLabel) parts.push(typeLabel);
  if (method.label?.trim()) parts.push(method.label.trim());
  return `${parts.join(' · ')}${method.isDefault ? ' (Default)' : ''}`;
}

const PROCESSOR_NAMES: Record<keyof AvailableProcessors, string> = {
  stripe: 'Stripe',
  payabli: 'Payabli',
};

/**
 * The tenant settings a saved-method charge is gated on: the `paymentMethods`
 * tab of `GET /settings/billing`.
 *
 * Deliberately not optional. The server fills `availableProcessors` in before
 * responding (absent tenant flags come back as both `true`), so a loaded
 * response always has a definite answer. The only way to be missing these is
 * that the settings request is still in flight or failed, and neither of those
 * means "everything is allowed". A caller without the response has nothing to
 * check against and must treat every method as not yet chargeable.
 */
export interface SavedMethodChargeSettings {
  availableProcessors: AvailableProcessors;
  acceptCard: boolean;
  acceptACH: boolean;
}

/**
 * Why this saved method cannot be charged right now, or `null` if it can.
 *
 * Mirrors the gates the invoice charge path (`initiatePayment` in operations)
 * applies, in the same order, rather than second-guessing them:
 *
 * 1. The payment rail. A stored bank account charges as ACH and is refused when
 *    `acceptACH` is off; anything else charges as a card (credit or debit) and
 *    is refused when `acceptCard` is off.
 * 2. The processor. `isPaymentProcessorEnabled` recognises only `stripe` and
 *    `payabli`. A tenant that migrated to Payabli keeps its old Stripe cards on
 *    file, and charging one returns `400 Payment processor "stripe" is not
 *    enabled for this tenant`.
 *
 * The picker has to say these before the operator commits, not after.
 */
export function savedMethodUnchargeableReason(
  method: SavedMethodProviderFields & Pick<SavedMethodDisplayFields, 'type'>,
  settings: SavedMethodChargeSettings,
): string | null {
  const provider = String(method.provider ?? '')
    .trim()
    .toLowerCase();
  if (provider !== 'stripe' && provider !== 'payabli') {
    return 'no payment processor on file';
  }
  if (isBankPaymentMethod(method)) {
    if (!settings.acceptACH) return 'ACH payments are turned off for this company';
  } else if (!settings.acceptCard) {
    return 'card payments are turned off for this company';
  }
  if (!settings.availableProcessors[provider]) {
    return `${PROCESSOR_NAMES[provider]} is turned off for this company`;
  }
  return null;
}

/**
 * Why this saved method cannot take a DEPOSIT, or `null` if it can.
 *
 * The same gates as an ordinary saved-method charge. A bank account used to be
 * refused on anything but Payabli here, mirroring a server rule that has since
 * been lifted: the settlement a bank deposit waits on never was Payabli-shaped,
 * and a Stripe tenant was left able to SAVE a bank account for a deposit it
 * could then never be charged for.
 */
export function depositUnchargeableReason(
  method: SavedMethodProviderFields & Pick<SavedMethodDisplayFields, 'type'>,
  settings: SavedMethodChargeSettings,
): string | null {
  return savedMethodUnchargeableReason(method, settings);
}

/**
 * Whether the processor has written this saved method off: HARD_DECLINED (the
 * issuer said never again) or INVALID (the stored credential is broken).
 * Mirrors `isUnchargeableDeclineState` in operations, which the automatic
 * charge engines gate on. SOFT_DECLINED is not written off — the retry ladder
 * exists for it — and neither is an absent state.
 *
 * Not a charge gate: the server does not refuse a written-off method on an
 * operator-initiated charge, so pickers keep listing it. It decides only what a
 * picker may pre-select on the operator's behalf.
 */
export function isWrittenOffDeclineState(state: string | null | undefined): boolean {
  return state === 'HARD_DECLINED' || state === 'INVALID';
}

/**
 * The saved method a picker may pre-select for the operator. A method with no
 * declines comes first, the default before the rest; only when none is clean
 * does a soft-declined one, which the retry ladder still covers, get chosen.
 * Written-off methods are never pre-selected: '' when every method is written
 * off, so the operator picks one explicitly rather than being handed a closed
 * account.
 */
export function preselectableSavedMethodId(
  methods: ReadonlyArray<{ id: string; isDefault?: boolean; declineState?: string | null }>,
): string {
  const clean = methods.filter((method) => isCleanDeclineState(method.declineState));
  const usable = methods.filter((method) => !isWrittenOffDeclineState(method.declineState));
  for (const pool of [clean, usable]) {
    const chosen = pool.find((method) => method.isDefault) ?? pool[0];
    if (chosen) return chosen.id;
  }
  return '';
}

/** Whether a saved method has no declines: HEALTHY, or no recorded state at all. */
function isCleanDeclineState(state: string | null | undefined): boolean {
  return !state || state === 'HEALTHY';
}

/**
 * The method a change-method dialog opens on: the plan's current method while it
 * is still listed and has no declines, else the usual pre-selection. The dialog
 * exists to replace a failing method, so it must not open on that method and
 * let Save re-submit it. Edit forms keep their saved value instead: there it is
 * a choice the operator already made.
 */
export function replacementSavedMethodId(
  methods: ReadonlyArray<{ id: string; isDefault?: boolean; declineState?: string | null }>,
  currentId: string | null | undefined,
): string {
  const current = currentId ? methods.find((method) => method.id === currentId) : undefined;
  if (current && isCleanDeclineState(current.declineState)) return current.id;
  return preselectableSavedMethodId(methods);
}

export function isSavedMethodChargeable(
  method: SavedMethodProviderFields & Pick<SavedMethodDisplayFields, 'type'>,
  settings: SavedMethodChargeSettings,
): boolean {
  return savedMethodUnchargeableReason(method, settings) === null;
}

/**
 * The sentence shown when the contact has saved methods but none of them is
 * chargeable. An empty or silently-filtered picker would be its own bug: the
 * operator would read "no saved methods" about cards they can see on the
 * contact, so name the distinct reasons and the way out.
 */
export function unchargeableSavedMethodsMessage(
  methods: Array<SavedMethodProviderFields & Pick<SavedMethodDisplayFields, 'type'>>,
  settings: SavedMethodChargeSettings,
): string {
  const reasons = [
    ...new Set(
      methods
        .map((method) => savedMethodUnchargeableReason(method, settings))
        .filter((reason): reason is string => reason !== null),
    ),
  ];
  const because = reasons.length ? ` — ${reasons.join('; ')}` : '';
  // Not "for this company's current processor": when the cause is a rail that
  // is switched off, a new card on the same processor is refused just the same.
  return `None of the saved payment methods on file can be charged${because}. Add a payment method this company currently accepts, or record the payment manually.`;
}
